import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ChartBase from './ChartBase';
import { resolveChartData, getItemValue, getItemLabel, getMaxValue } from './ChartData';
import '../../styles/chart-styles.css';

const BarChart = ({ 
  data, 
  moduleId, 
  dataIndex, 
  title, 
  subtitle,
  showLabels = true, 
  animate = true,
  barColor = null,
  className = '',
  ...props 
}) => {
  const { getModuleData } = useTheme();
  
  // Get the entire module data array
  const moduleData = getModuleData(moduleId);
  const chartArray = resolveChartData(data, moduleData);
  const maxValue = getMaxValue(chartArray);

  const getBarHeight = (value) => {
    const height = (value / maxValue) * 100;
    return `${height}%`;
  };

  const getBarHeightPixels = (value) => {
    const percentage = (value / maxValue) * 100;
    const containerHeight = 180; // Available height for bars (after accounting for labels)
    return (percentage / 100) * containerHeight;
  };

  return (
    <ChartBase title={title} subtitle={subtitle}>
      {/* Chart Content */}
      <div className="flex flex-col h-full">
        {/* Bar container - fills available space */}
        <div className="flex-1 flex items-end justify-center space-x-2" style={{ height: '160px' }}>
          {chartArray.map((item, index) => {
            const value = getItemValue(item);
            const label = getItemLabel(item, index);
            
            return (
              <div 
                key={index} 
                className="flex flex-col items-center flex-1 justify-end"
                data-height={getBarHeight(value)}
                data-maxheight={`${maxValue}%`}
                data-value={value}
              >
                <div 
                  className="chart--bar-item w-full rounded-t transition-all duration-300 hover:opacity-80"
                  style={{ 
                    height: `${getBarHeightPixels(value)}px`,
                    backgroundColor: 'var(--theme-primary)',
                    minHeight: '2px'
                  }}
                />
              </div>
            );
          })}
        </div>
        
        {/* Labels container - consistent height for all labels */}
        <div class = "labelcontainer" className="flex justify-center space-x-2 mt-2">
          {chartArray.map((item, index) => {
            const value = getItemValue(item);
            const label = getItemLabel(item, index);
            
            return (
              <div 
                key={`label-${index}`} 
                className="flex flex-col items-center flex-1 text-center"
              >
                <span className="text-xs theme-primary font-medium">
                  {value}
                </span>
                {label && (
                  <span className="text-xs theme-primary opacity-70">
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </ChartBase>
  );
};

// Internal BarChart rendering logic
const BarChartRenderer = ({ chartArray, getBarHeight, getBarHeightPixels, maxValue }) => {
  return (
    <div className="chart--bar w-full h-64 theme-bg rounded-lg shadow-md p-4">
      <div className="flex flex-col h-full">
        {/* Bar container - fills available space */}
        <div className="flex-1 flex items-end justify-center space-x-2">
          {chartArray.map((item, index) => {
            const value = getItemValue(item);
            const label = getItemLabel(item, index);
            
            return (
              <div 
                key={index} 
                className="flex flex-col items-center flex-1 justify-end"
                data-height={getBarHeight(value)}
                data-maxheight={`${maxValue}`}
                data-value={value}
              >
                <div 
                  className="chart--bar-item w-full rounded-t transition-all duration-300 hover:opacity-80"
                  style={{ 
                    height: `${getBarHeightPixels(value)}px`,
                    backgroundColor: 'var(--theme-primary)',
                    minHeight: '2px'
                  }}
                />
              </div>
            );
          })}
        </div>
        
        {/* Labels container - consistent height for all labels */}
        <div class = "labelcontainer" className="flex justify-center space-x-2 mt-2">
          {chartArray.map((item, index) => {
            const value = getItemValue(item);
            const label = getItemLabel(item, index);
            
            return (
              <div 
                key={`label-${index}`} 
                className="flex flex-col items-center flex-1 text-center"
              >
                <span className="text-xs theme-primary font-medium">
                  {value}
                </span>
                {label && (
                  <span className="text-xs theme-primary opacity-70">
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BarChart;
